import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import IngredientsModal from '../components/IngredientsModal';
import EditFormulaModal from '../components/EditFormulaModal';
import AddIngredientModal from '../components/AddIngredientModal';
import FormulaUpdateConfirmation from '../components/FormulaUpdateConfirmation';
import useComparisonFormulaDraft from '../../../hooks/useComparisonFormulaDraft';
import { useUpdateDashboardFormulaMutation } from '../../../features/customerApiSlice';

const DashboardFormulaEditor = ({
  formula: initialFormula,
  lang,
  name,
  handleClose,
  handleSaved,
  startAtEdit = false,
}) => {
  const { t } = useTranslation();
  const [formula] = useState(initialFormula);
  const [step, setStep] = useState(startAtEdit ? 'edit' : 'ingredients');
  const [updateFormula, { isLoading: isUpdating }] = useUpdateDashboardFormulaMutation();
  const draft = useComparisonFormulaDraft({
    ingredients: formula.ingredients, pricing: formula.pricing, lang,
  });
  const pricing = formula.pricing;
  const formatAmount = (amount, currency = pricing.currency) =>
    new Intl.NumberFormat(lang, {
      style: 'currency', currency: (currency || 'eur').toUpperCase(),
      maximumFractionDigits: 2,
    }).format(amount);
  const cadence = pricing.cadenceMonths === 3 ? t('EveryThreeMonths') : `/${t('Month')}`;
  const currentPrice = `${formatAmount(pricing.currentPrice)} ${cadence}`;
  const newPrice = `${formatAmount(draft.pricing.newAmount)} ${cadence}`;
  const effectiveDate = new Date(pricing.currentPeriodEnd).toLocaleDateString(lang);
  const isUpdateBlocked = isUpdating || draft.isInvalid || !draft.hasFormulaChanged ||
    draft.isLoadingCatalog || draft.isCatalogError;
  const handleReview = () => {
    if (!isUpdateBlocked) setStep('confirm');
  };
  const handleConfirm = async () => {
    if (isUpdateBlocked) return;
    try {
      const result = await updateFormula({
        userSubscriptionId: formula.userSubscriptionId,
        expectedQuizId: formula.quizId,
        ingredientDraft: draft.ingredientDraft,
        lang,
        previewedAmount: draft.pricing.newAmount,
        previewedCurrentPrice: pricing.currentPrice,
        previewedEffectiveAt: pricing.currentPeriodEnd,
      }).unwrap();
      if (!result.scheduled) throw new Error();
      toast.success(t('DashboardFormula_Success'));
      await handleSaved();
    } catch (error) {
      if ([400, 409].includes(error?.status)) setStep('edit');
      toast.error(error?.data?.message || t('DashboardFormula_Error'));
    }
  };

  return (
    <>
      <IngredientsModal
        open={step === 'ingredients'}
        handleClose={handleClose}
        data={draft.data}
        allIngredients={draft.allIngredients}
        name={name}
        focusAreaCount={formula.focusAreaCount}
        onOpenEdit={() => setStep('edit')}
      />
      <EditFormulaModal
        open={step === 'edit'}
        handleClose={() => {
          if (isUpdating) return;
          // Opened straight at edit: closing leaves the editor instead of landing on the preview.
          if (startAtEdit) handleClose();
          else setStep('ingredients');
        }}
        data={draft.data}
        ingredients={formula.ingredients}
        hasFormulaChanged={draft.hasFormulaChanged}
        setAddIngredientsModal={() => setStep('add')}
        allIngredients={draft.allIngredients}
        onRemoveQuantity={draft.onRemoveQuantity}
        onAddQuantity={draft.onAddQuantity}
        prices={draft.prices}
        removeIngredientFromBin={draft.removeIngredientFromBin}
        onInputChange={draft.onInputChange}
        onReset={draft.handleReset}
        name={name}
        onReview={handleReview}
        reviewMessage={
          draft.isCatalogError
            ? t('DashboardFormula_CatalogError')
            : draft.isInvalid
              ? t('QuizComparison_DraftError')
              : null
        }
        renewalPriceMessage={t('DashboardFormula_PricePreview', { currentPrice, newPrice })}
        isReviewDisabled={isUpdateBlocked}
      />
      <AddIngredientModal
        open={step === 'add'}
        handleClose={() => setStep('edit')}
        allIngredients={draft.allIngredients}
        data={draft.data}
        removeIngredient={draft.removeIngredient}
        addIngredient={draft.addIngredient}
      />
      {step === 'confirm' && (
        <FormulaUpdateConfirmation
          pricing={pricing} currentPrice={currentPrice} newPrice={newPrice}
          effectiveDate={effectiveDate} formatAmount={formatAmount}
          body={t('DashboardFormula_ConfirmBody', {
            currentPrice: formatAmount(pricing.currentPrice),
            newPrice: formatAmount(draft.pricing.newAmount),
            cadence, date: effectiveDate,
          })}
          isUpdating={isUpdating} isDisabled={isUpdateBlocked}
          handleClose={() => { if (!isUpdating) setStep('edit'); }}
          handleConfirm={handleConfirm}
        />
      )}
    </>
  );
};

export default DashboardFormulaEditor;
